import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { brandColours as defaultColours, brandSurfaces as defaultSurfaces } from '@/mocks/branding';
import {
  loadBrandingSnapshot,
  saveBrandingSnapshot,
  loadLogos,
  saveLogos,
} from '@/utils/brandingStore';
import type { BrandProfile, BrandRole, BrandSurface } from '@/types/portal';
import { api } from '@/utils/api';
import { useAuth } from './AuthContext';
import { BrandContext, type BrandContextValue } from './BrandContext';

// ---------------------------------------------------------------------------
// CSS variable injection
// ---------------------------------------------------------------------------

/**
 * Converts a hex colour (#RRGGBB) to OKLCH channel values for the design-system
 * CSS variables. We do a quick sRGB→linear→XYZ→OKLab→OKLCH conversion.
 */
function hexToOklch(hex: string): string {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.slice(0, 2), 16) / 255;
  const g = parseInt(clean.slice(2, 4), 16) / 255;
  const b = parseInt(clean.slice(4, 6), 16) / 255;

  // sRGB → linear light
  const toLinear = (c: number) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  const rl = toLinear(r);
  const gl = toLinear(g);
  const bl = toLinear(b);

  // linear sRGB → OKLab (Björn Ottosson's matrix)
  const l = 0.4122214708 * rl + 0.5363325363 * gl + 0.0514459929 * bl;
  const m = 0.2119034982 * rl + 0.6806995451 * gl + 0.1073969566 * bl;
  const s = 0.0883024619 * rl + 0.2817188376 * gl + 0.6299787005 * bl;

  const lc = Math.cbrt(l);
  const mc = Math.cbrt(m);
  const sc = Math.cbrt(s);

  const L = 0.2104542553 * lc + 0.793617785 * mc - 0.0040720468 * sc;
  const a = 1.9779984951 * lc - 2.428592205 * mc + 0.4505937099 * sc;
  const bk = 0.0259040371 * lc + 0.7827717662 * mc - 0.808675766 * sc;

  const C = Math.sqrt(a * a + bk * bk);
  const H = (Math.atan2(bk, a) * 180) / Math.PI;
  const hue = H < 0 ? H + 360 : H;

  return `${L.toFixed(4)} ${C.toFixed(4)} ${hue.toFixed(2)}`;
}

/**
 * Interpolate hex towards white or black to build a scale step.
 * `amount` is signed: positive walks towards white, negative towards black.
 * 0 returns the original colour untouched.
 */
function scaleStep(hex: string, amount: number): string {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.slice(0, 2), 16) || 0;
  const g = parseInt(clean.slice(2, 4), 16) || 0;
  const b = parseInt(clean.slice(4, 6), 16) || 0;

  const target = amount >= 0 ? 255 : 0;
  const factor = Math.min(Math.abs(amount), 0.97);

  const mix = (ch: number) =>
    Math.max(0, Math.min(255, Math.round(ch + (target - ch) * factor)))
      .toString(16)
      .padStart(2, '0');

  return `#${mix(r)}${mix(g)}${mix(b)}`;
}

/**
 * The scale ramp for one hue. Positive mix = tint towards white (lighter),
 * negative mix = shade towards black (darker). Index 500 is the raw brand hex.
 */
const SCALE_STEPS: [number, number][] = [
  [50, 0.94],
  [100, 0.88],
  [200, 0.72],
  [300, 0.54],
  [400, 0.26],
  [500, 0], // anchor — the raw brand hex
  [600, -0.16],
  [700, -0.32],
  [800, -0.46],
  [900, -0.6],
  [950, -0.72],
];

/**
 * Write (or clear) one role's computed scale block inside the override sheet.
 * When `isDefault` is true the block is removed so the hand-tuned scale in the
 * base stylesheet applies untouched — this keeps the platform pixel-identical
 * at its default branding.
 */
function writeRoleBlock(
  role: string,
  hex: string,
  isDefault: boolean,
  styleEl: HTMLStyleElement,
) {
  const marker = `/* brand-scale:${role} */`;
  const escaped = marker.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const re = new RegExp(`\\n?${escaped}[\\s\\S]*?\\}`, 'g');

  // Always clear any existing block for this role first
  let text = (styleEl.textContent ?? '').replace(re, '');

  if (!isDefault) {
    const vars = SCALE_STEPS.map(([step, amount]) => {
      const colour = step === 500 ? hex : scaleStep(hex, amount);
      return `  --${role}-${step}: ${hexToOklch(colour)};`;
    }).join('\n');
    text += `\n${marker}\n:root {\n${vars}\n}`;
  }

  styleEl.textContent = text;
}

const isDefaultColour = (role: BrandRole, hex: string) =>
  hex.trim().toLowerCase() === DEFAULT_COLOURS[role].trim().toLowerCase();

// ---------------------------------------------------------------------------
// Default state helpers
// ---------------------------------------------------------------------------

const colourOf = (role: BrandRole): string =>
  defaultColours.find((entry) => entry.role === role)?.hex ?? '#8A0E49';

const DEFAULT_COLOURS: Record<BrandRole, string> = {
  primary: colourOf('primary'),
  accent: colourOf('accent'),
  secondary: colourOf('secondary'),
};

const DEFAULT_SURFACES: BrandSurface[] = defaultSurfaces.map((s) => ({ ...s }));

/** The one logo with a database column (`agencies.logo_url`); the others stay in the browser. */
const SERVER_LOGO = 'mark';

/** `GET/PUT /cpaas/settings/branding`. A null colour means "never set": use the platform palette. */
interface BrandingResponse {
  profile: BrandProfile;
  colours: Record<BrandRole, string | null>;
  logos: Record<string, string>;
}

/** `GET /cpaas/settings/branding/public` — the agency behind a verified custom domain, if any. */
interface PublicBrandingResponse {
  agency: {
    name: string;
    logoInitials: string;
    colours: Record<BrandRole, string | null>;
    logos: Record<string, string>;
  } | null;
}

/** Before sign-in, off an agency's own domain: the platform, as Laravel's login shows it. */
const PLATFORM_PROFILE: BrandProfile = {
  name: 'Twiva',
  legalName: '',
  tagline: '',
  supportEmail: '',
  website: '',
  baseCurrency: 'KES',
  markets: [],
  logoInitials: 'TW',
  domain: '',
  domainStatus: 'Not connected',
};

const withoutServerLogo = (logos: Record<string, string>) => {
  const rest = { ...logos };
  delete rest[SERVER_LOGO];
  return rest;
};

export function BrandProvider({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const [profile, setProfile] = useState<BrandProfile>({ ...PLATFORM_PROFILE });
  const [colours, setColours] = useState<Record<BrandRole, string>>({ ...DEFAULT_COLOURS });
  const [surfaces, setSurfaces] = useState<BrandSurface[]>(DEFAULT_SURFACES.map((s) => ({ ...s })));
  const [logos, setLogos] = useState<Record<string, string>>({});
  const [savedProfile, setSavedProfile] = useState<BrandProfile>({ ...PLATFORM_PROFILE });
  const [savedColours, setSavedColours] = useState<Record<BrandRole, string>>({ ...DEFAULT_COLOURS });
  const [savedSurfaces, setSavedSurfaces] = useState<BrandSurface[]>(DEFAULT_SURFACES.map((s) => ({ ...s })));
  const [savedLogos, setSavedLogos] = useState<Record<string, string>>({});
  const [dirty, setDirty] = useState(false);

  // Style element for injecting CSS variable overrides
  const styleRef = useRef<HTMLStyleElement | null>(null);

  useEffect(() => {
    const el = document.createElement('style');
    el.id = 'brand-scale-overrides';
    document.head.appendChild(el);
    styleRef.current = el;

    return () => {
      el.remove();
    };
  }, []);

  /** Take the agency's saved branding from the API as both current and saved state. */
  const applyServer = useCallback((data: BrandingResponse, browserLogos: Record<string, string>) => {
    const nextColours = {
      primary: data.colours.primary ?? DEFAULT_COLOURS.primary,
      accent: data.colours.accent ?? DEFAULT_COLOURS.accent,
      secondary: data.colours.secondary ?? DEFAULT_COLOURS.secondary,
    };
    const nextLogos = { ...withoutServerLogo(browserLogos), ...data.logos };
    setProfile({ ...data.profile });
    setSavedProfile({ ...data.profile });
    setColours(nextColours);
    setSavedColours({ ...nextColours });
    setLogos(nextLogos);
    setSavedLogos({ ...nextLogos });
    setDirty(false);
  }, []);

  // Load the signed-in agency's branding. Signed out, the platform defaults apply,
  // unless the portal is on an agency's verified custom domain.
  useEffect(() => {
    let active = true;
    if (status !== 'signedIn') {
      setProfile({ ...PLATFORM_PROFILE });
      setSavedProfile({ ...PLATFORM_PROFILE });
      setColours({ ...DEFAULT_COLOURS });
      setSavedColours({ ...DEFAULT_COLOURS });
      setSurfaces(DEFAULT_SURFACES.map((s) => ({ ...s })));
      setSavedSurfaces(DEFAULT_SURFACES.map((s) => ({ ...s })));
      setLogos({});
      setSavedLogos({});
      setDirty(false);
      api<PublicBrandingResponse>(`/settings/branding/public?domain=${encodeURIComponent(window.location.hostname)}`)
        .then(({ agency }) => {
          if (!active || !agency) return;
          const branded = { ...PLATFORM_PROFILE, name: agency.name, logoInitials: agency.logoInitials };
          const nextColours = {
            primary: agency.colours.primary ?? DEFAULT_COLOURS.primary,
            accent: agency.colours.accent ?? DEFAULT_COLOURS.accent,
            secondary: agency.colours.secondary ?? DEFAULT_COLOURS.secondary,
          };
          setProfile(branded);
          setSavedProfile({ ...branded });
          setColours(nextColours);
          setSavedColours({ ...nextColours });
          setLogos({ ...agency.logos });
          setSavedLogos({ ...agency.logos });
        })
        .catch(() => {
          /* Keep the platform defaults. */
        });
      return () => {
        active = false;
      };
    }

    const stored = loadBrandingSnapshot();
    if (stored?.surfaces && stored.surfaces.length > 0) {
      setSurfaces(stored.surfaces.map((s) => ({ ...s })));
      setSavedSurfaces(stored.surfaces.map((s) => ({ ...s })));
    }
    Promise.all([api<BrandingResponse>('/settings/branding'), loadLogos()])
      .then(([data, browserLogos]) => {
        if (active) applyServer(data, browserLogos);
      })
      .catch(() => {
        /* Keep the platform defaults; the branding page shows its own errors. */
      });
    return () => {
      active = false;
    };
  }, [status, applyServer]);

  // Re-inject CSS variables whenever colours change
  useEffect(() => {
    if (!styleRef.current) return;
    (['primary', 'accent', 'secondary'] as BrandRole[]).forEach((role) =>
      writeRoleBlock(role, colours[role], isDefaultColour(role, colours[role]), styleRef.current!),
    );
  }, [colours]);

  const updateField = (key: keyof BrandProfile, value: string) => {
    setProfile((c) => ({ ...c, [key]: value }));
    setDirty(true);
  };

  const setMarkets = (markets: string[]) => {
    setProfile((c) => ({ ...c, markets }));
    setDirty(true);
  };

  const applyColour = (role: BrandRole, hex: string) => {
    setColours((c) => ({ ...c, [role]: hex }));
    setDirty(true);
  };

  const applyPreset = (preset: { primary: string; accent: string; secondary: string }) => {
    setColours({ primary: preset.primary, accent: preset.accent, secondary: preset.secondary });
    setDirty(true);
  };

  const toggleSurface = (id: string) => {
    setSurfaces((c) =>
      c.map((s) => (s.id === id ? { ...s, enabled: !s.enabled } : s)),
    );
    setDirty(true);
  };

  const setLogo = (id: string, dataUrl: string) => {
    setLogos((c) => ({ ...c, [id]: dataUrl }));
    setDirty(true);
  };

  const clearLogo = (id: string) => {
    setLogos((c) => {
      const next = { ...c };
      delete next[id];
      return next;
    });
    setDirty(true);
  };

  const save = async () => {
    // The logo mark is sent only when it changed: a new data URL, or null to remove it.
    const mark = logos[SERVER_LOGO];
    const markChanged = mark !== savedLogos[SERVER_LOGO];
    const data = await api<BrandingResponse>('/settings/branding', {
      method: 'PUT',
      body: JSON.stringify({
        profile,
        colours,
        ...(markChanged ? { logo: mark ?? null } : {}),
      }),
    });
    const browserLogos = withoutServerLogo(logos);
    saveBrandingSnapshot({ surfaces });
    await saveLogos(browserLogos);
    setSavedSurfaces(surfaces.map((s) => ({ ...s })));
    applyServer(data, browserLogos);
  };

  const reset = () => {
    setProfile({ ...savedProfile });
    setColours({ ...savedColours });
    setSurfaces(savedSurfaces.map((s) => ({ ...s })));
    setLogos({ ...savedLogos });
    setDirty(false);
  };

  const resetToDefaults = async () => {
    const data = await api<BrandingResponse>('/settings/branding/reset', { method: 'POST' });
    saveBrandingSnapshot({ surfaces: DEFAULT_SURFACES });
    await saveLogos({});
    setSurfaces(DEFAULT_SURFACES.map((s) => ({ ...s })));
    setSavedSurfaces(DEFAULT_SURFACES.map((s) => ({ ...s })));
    applyServer(data, {});
  };

  const value = useMemo<BrandContextValue>(
    () => ({
      profile,
      colours,
      surfaces,
      logos,
      dirty,
      updateField,
      setMarkets,
      applyColour,
      applyPreset,
      toggleSurface,
      setLogo,
      clearLogo,
      save,
      reset,
      resetToDefaults,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [profile, colours, surfaces, logos, dirty],
  );

  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
}
